import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  ArrowRight,
  ShieldCheck,
  CreditCard,
  CheckCircle,
} from "@phosphor-icons/react";
import { LoginAuthContext } from "Context/Authentication/LoginAuth";
import useApiQuery from "Hooks/useApiQuery";
import Container from "Components/Container/Container";

const HomeBanner = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isLoggedIn, isVerified, isAuthenticated } = useContext(LoginAuthContext);
  const [dismissed, setDismissed] = useState(false);
  const isRTL = i18n.language === "ar";

  const { data } = useApiQuery("home/home-banner");
  const banner = data?.data;

  if (!isLoggedIn || isAuthenticated || dismissed || !banner || !banner.isActive) {
    return null;
  }

  const isUnverified = !isVerified;
  const title = isUnverified ? banner.unverified_title : banner.no_plan_title;
  const text = isUnverified ? banner.unverified_text : banner.no_plan_text;
  const features = isUnverified ? [] : (banner.no_plan_features || []);
  const ctaPath = isUnverified ? "/verify-email" : "/plans";
  const ctaLabel = isUnverified
    ? (t("general.continue_setup") || "Continue Setup")
    : (t("general.choose_plan") || "Choose Plan");
  const Icon = isUnverified ? ShieldCheck : CreditCard;

  if (!title && !text) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.4 }}
      >
        <Container>
          {/*
            Mobile (<lg): matches the Dalilouki mobile app HomeBanner — dark gradient,
            faint white border, primary-color corner glow, square icon badge,
            checkmark feature rows, full-width CTA at the bottom.
            Desktop (lg+): original horizontal primary-tinted layout.
          */}
          <div
            className="
              relative overflow-hidden rounded-2xl p-5 lg:p-6
              bg-gradient-to-br from-[#1c1a1c] to-[#0f0e0f]
              border border-white/10
              lg:bg-gradient-to-r lg:from-primary/20 lg:via-primary/10 lg:to-transparent
              lg:border-primary/30
            "
          >
            {/* Corner glow — mobile only */}
            <div
              aria-hidden
              className="lg:hidden absolute -top-16 -end-16 w-48 h-48 rounded-full bg-primary/25 blur-3xl pointer-events-none"
            />

            {/* Dismiss */}
            <button
              onClick={() => setDismissed(true)}
              className="absolute top-3 end-3 z-10 text-gray-400 hover:text-white transition-colors p-1"
              aria-label="Dismiss"
            >
              <X size={18} />
            </button>

            <div className="relative flex flex-col lg:flex-row items-start lg:items-center gap-4">
              {/* Icon badge */}
              <div
                className="
                  w-11 h-11 rounded-xl bg-white/10 border border-white/20
                  flex items-center justify-center flex-shrink-0
                  lg:w-12 lg:h-12 lg:rounded-full lg:bg-primary/20 lg:border-0
                "
              >
                <Icon size={22} className="text-primary" />
              </div>

              {/* Content */}
              <div className="flex-1 w-full">
                {title && (
                  <h3 className="text-white font-extrabold text-[22px] leading-7 mb-1.5 pe-7 lg:text-lg lg:font-bold lg:leading-normal lg:pe-0 lg:mb-1">
                    {title}
                  </h3>
                )}
                {text && (
                  <p className="text-gray-400 text-sm leading-5 mb-0">
                    {text}
                  </p>
                )}

                {features.length > 0 && (
                  <>
                    {/* Mobile: checkmark rows (matches app) */}
                    <div className="lg:hidden flex flex-col gap-2.5 mt-4">
                      {features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2.5">
                          <CheckCircle
                            size={16}
                            weight="fill"
                            className="text-primary flex-shrink-0"
                          />
                          <span className="text-white text-sm font-medium">
                            {feature}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Desktop: pill badges (unchanged) */}
                    <div className="hidden lg:flex flex-wrap gap-3 mt-3">
                      {features.map((feature, idx) => (
                        <span
                          key={idx}
                          className="text-xs bg-white/10 text-gray-300 px-3 py-1 rounded-full"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </>
                )}

                {/* CTA — mobile: full-width at bottom of card (matches app) */}
                <button
                  onClick={() => navigate(ctaPath)}
                  className="
                    lg:hidden w-full mt-5
                    flex items-center justify-center gap-2
                    bg-primary hover:bg-primary/90 text-white
                    py-3.5 rounded-xl text-[15px] font-bold
                    transition-colors duration-300 min-h-[48px]
                  "
                >
                  {ctaLabel}
                  <ArrowRight size={18} className={isRTL ? "rotate-180" : ""} />
                </button>
              </div>

              {/* CTA — desktop: inline on the right (unchanged) */}
              <button
                onClick={() => navigate(ctaPath)}
                className="
                  hidden lg:flex bg-primary hover:bg-primary/90 text-white
                  px-6 py-3 rounded-xl text-sm font-semibold
                  transition-all duration-300 items-center gap-2 flex-shrink-0 min-h-[44px]
                "
              >
                {ctaLabel}
                <ArrowRight size={16} className={isRTL ? "rotate-180" : ""} />
              </button>
            </div>
          </div>
        </Container>
      </motion.div>
    </AnimatePresence>
  );
};

export default HomeBanner;
